import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import {
  Body,
  Card,
  PageHeader,
  Screen,
  ScreenBody,
  SectionHead,
  Stack,
} from '@leanin/ui';
import type { IconName, Tone } from '@leanin/ui';
import type { components } from '@leanin/api-client';
import { accountKeys } from '@/lib/queries/account';
import { SettingsErrorShell } from '@/components/settings/SettingsErrorShell';
import { DataRequestActionTile } from './-components/DataRequestActionTile';
import { DataRequestTable } from './-components/DataRequestTable';

type DsrKind = components['schemas']['DsrKind'];

type ActionDef = {
  readonly kind: DsrKind;
  readonly icon: IconName;
  readonly tone: Tone;
};

const ACTIONS: readonly ActionDef[] = [
  { kind: 'access', icon: 'book', tone: 'navy' },
  { kind: 'rectification', icon: 'edit', tone: 'emerald' },
  { kind: 'erasure', icon: 'trash', tone: 'burgundy' },
  { kind: 'restriction', icon: 'lock', tone: 'yellow' },
  { kind: 'portability', icon: 'upload', tone: 'neutral' },
  { kind: 'objection', icon: 'flag', tone: 'orange' },
];

export const Route = createFileRoute('/_authed/settings/dsr/')({
  component: SettingsDSRPage,
});

function SettingsDSRPage() {
  const t = useTranslations('Settings');
  const query = useQuery({
    ...accountKeys.dsrRequests(),
    retry: false,
  });
  if (query.isError) {
    return (
      <SettingsErrorShell
        title={t('dsr.errorTitle')}
        message={t('dsr.loadError')}
      />
    );
  }
  if (query.isLoading) return null;
  return (
    <Screen>
      <ScreenBody gap="2xl">
        <PageHeader
          eyebrow={t('back.settings')}
          title={t('dsr.title')}
          subtitle={t('dsr.subtitle')}
          back={{ label: t('back.settings'), href: '/settings' }}
        />
        <Stack gap="md">
          <SectionHead title={t('dsr.make.heading')} />
          <Body color="mute">{t('dsr.make.intro')}</Body>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
              // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- CSS grid layout; no Box prop for grid containers
              gap: 'var(--space-md)',
            }}
          >
            {ACTIONS.map((a) => (
              <DataRequestActionTile
                key={a.kind}
                kind={a.kind}
                title={t(`dsr.kinds.${a.kind}.title`)}
                description={t(`dsr.kinds.${a.kind}.description`)}
                icon={a.icon}
                tone={a.tone}
              />
            ))}
          </div>
          <Body size="sm" color="mute">
            {t('dsr.make.footer')}
          </Body>
        </Stack>

        <Stack gap="md">
          <SectionHead title={t('dsr.active.heading')} />
          <Card pad="default">
            <Stack gap="md">
              <DataRequestTable />
              <Body size="sm" color="mute">
                {t.rich('dsr.active.footer', {
                  em: (chunks) => <em>{chunks}</em>,
                })}
              </Body>
            </Stack>
          </Card>
        </Stack>
      </ScreenBody>
    </Screen>
  );
}
